/*!
  Pioneer WFRIA Select v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["Select"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("Select") < 0) {
    window.__PIONEER_GLOBAL__.components.push("Select");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["children", "className", "customStrings", "customValidators", "defaultValue", "disabled", "fieldClassName", "id", "label", "labelClassName", "name", "nativeValidationMessages", "onBlur", "onChange", "onValueChange", "required", "noAsterisk", "noEllipsisForPlaceholder", "hint", "ariaLabel", "selectMenuWidth", "labelHidden", "value", "wrap", "autoMenuWidth", "menuzIndex", "errorMessageCallback", "selectTriggerStyles", "expandIconSize"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import clsx from "clsx";
import debounce from "lodash.debounce";
import throttle from "lodash.throttle";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";
import { KEYBOARD_KEYS, KEYBOARD_NAVIGATOR_MODE, MENUITEM, MENUITEMGROUP, ZINDEX, ZOOM_400_MAX_WIDTH } from "../common/constants";
import { useControlOrUnControl, useFocusFieldOnError, useOnEscape, useUniqueId } from "../common/hooks";
import { invalidIf } from "../common/proptypes";
import { __uniqueID, isActiveElementFocusable, registerField, safeMerge } from "../common/utilities";
import { FormContext } from "../Form/FormContext";
import FieldFooter from "../private/FieldFooter/FieldFooter";
import KeyboardNavigator from "../private/KeyboardNavigator/KeyboardNavigator";
import OptGroup from "../private/OptGroup";
import Popup from "../private/Popup/Popup";
import { OVERRIDE, SELECT_BACKGROUND_SCROLL_CLASS } from "../themes/common/classes";
import { BALLOON_POSITION, MOBILE_BREAKPOINT, NINETY_PERCENT, REQUIRED_TEXT, RESIZE_DEBOUNCE_TIME, SCROLL_ATTACH_TIME, SCROLL_THROTTLE_TIME, SEARCH_DEBOUNCE_TIME } from "./constants";
import extractTheme from "./extractTheme";
import STRINGS from "./strings";
import { Button, IconArrowClose, IconExpand, IconRequired, IconSubAccount, MenuItem } from "..";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
export const TEMPORARY_HEIGHT_BUFFER = 60;

function SelectFooter(_ref) {
  let {
    displayErrors,
    errors,
    errorId,
    footerErrorStyle,
    footerStyle,
    hint,
    hintId,
    label,
    name,
    hintCSS
  } = _ref;
  let fieldFooterProps = null;

  if (displayErrors && errors[name]) {
    fieldFooterProps = {
      displayErrors,
      label,
      footerErrorStyle,
      message: errors[name].message,
      id: errorId
    };
  } else if (hint) {
    fieldFooterProps = {
      id: hintId,
      hint,
      css: hintCSS
    };
  }

  return fieldFooterProps ? _jsx(FieldFooter, _objectSpread(_objectSpread({}, fieldFooterProps), {}, {
    footerStyle: footerStyle
  })) : null;
}
/**
 * Select is a React component which replaces the native HTML select control.
 * It is a drop-down field that provides a list of menu options for the user to select.
 * The field, menu and the selection interaction have been designed to be consistent,
 * accessible and usable across touch and desktop devices.
 */
// eslint-disable-next-line complexity


const Select = /*#__PURE__*/React.forwardRef((props, userRef) => {
  const {
    children,
    className,
    customStrings,
    customValidators,
    // eslint-disable-line no-unused-vars
    defaultValue: propDefaultValue,
    disabled,
    fieldClassName,
    id: userPassedId,
    label,
    labelClassName: labelClassNameProp,
    name,
    nativeValidationMessages,
    // eslint-disable-line no-unused-vars
    onBlur,
    onChange,
    // eslint-disable-line no-unused-vars
    onValueChange,
    required,
    noAsterisk,
    noEllipsisForPlaceholder,
    hint,
    ariaLabel,
    selectMenuWidth,
    labelHidden,
    value: userPassedValue,
    wrap,
    autoMenuWidth,
    menuzIndex,
    errorMessageCallback,
    // eslint-disable-line no-unused-vars
    selectTriggerStyles,
    expandIconSize
  } = props,
        elementProps = _objectWithoutProperties(props, _excluded);

  const labelRef = useRef();
  const selectRef = useRef();
  const wrapperNodeRef = useRef();
  const popupRef = useRef();
  const triggerRef = useRef();
  const [isDisabled, setIsDisabled] = useState(disabled); // isBlurred is used to force a re-render for AT error announcement.

  const [isBlurred, setIsBlurred] = useState(false);
  const [elementToFocusOnClose, setElementToFocusOnClose] = useState(null);
  const errorId = useUniqueId("pnr-text-select-error-");

  const componentStrings = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  const {
    requiredErrorMessage,
    instructionalText
  } = componentStrings;
  const {
    register,
    errors = {},
    isSubmitting,
    resetDone,
    setValue,
    trigger,
    unregister,
    validateField,
    validateTimestamp
  } = useContext(FormContext) || {};
  useFocusFieldOnError(selectRef, triggerRef, isSubmitting);
  const [balloonMaxHeight, setBalloonMaxHeight] = useState(0);
  const [balloonPosition, setBalloonPosition] = useState(BALLOON_POSITION.BOTTOM);
  /**
   * useControlOrUnControl hook returns the getter and setter
   * methods for updating the prop value, renamed getPropValue to
   * getSelectedValue for sake of readability.
   */

  const [getSelectedValue, setSelectedValue] = useControlOrUnControl({
    unControlledPropValue: propDefaultValue,
    controlledPropValue: userPassedValue
  });
  const currentSelectedValue = getSelectedValue();

  if (selectRef.current) {
    selectRef.current.value = currentSelectedValue;
  }
  /**
   * Errors should be first shown on blur, and then on change.
   * This state "displayErrors" is used to that effect: even
   * if the Form is reporting errors, we do not show them
   * before onBlur is triggered and change this state.
   *
   * This state is also reverted back to false on reset.
   */


  const [displayErrors, setDisplayErrors] = useState(false);
  const theme = useTheme();
  const [open, setOpen] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [triggerNodeWidth, setTriggerNodeWidth] = useState(0);
  const placeholder = customStrings !== null && customStrings !== void 0 && customStrings.placeholder ? customStrings.placeholder : STRINGS.placeholder;
  let selectedOptionLabel;
  /**
   * Maintaining the list items value and it's corresponding text
   * in a map. This is to ensure to get the selected item
   * from this object rather than querying the DOM for it.
   */

  const itemsMap = useRef({});
  const disabledItemsMap = useRef({}); // Using as `this` reference of class component.

  const thisRef = useRef({
    activeElementFocusable: false,
    currentIndex: 0,
    balloonNode: null,
    itemsText: [],
    scrollListenerTimeout: null,
    scrollCount: 0,
    searchQuery: "",
    selectWasClicked: false,
    triggerNode: null,
    uniqueId: useUniqueId()
  });
  const {
    uniqueId
  } = thisRef.current;
  const triggerId = `pnr-select__trigger-${uniqueId}`;
  const [balloonWidth, setBalloonWidth] = useState();
  const hintId = `pnr-select-hint-${uniqueId}`;
  const popupId = `pnr-select-popup-${uniqueId}`;
  const containerClassNames = clsx(className, "pnr-select");
  const selectedClass = clsx({
    "pnr-select__item--selected": !currentSelectedValue
  });
  selectedClass && (thisRef.current.currentIndex = 0);
  /**
   * getElementWidth method returns the width of the element
   * @param {el} The element node
   * @return {width: Number}
   */

  const getElementWidth = el => {
    const {
      width
    } = el.getBoundingClientRect();
    return {
      width
    };
  };
  /**
   * Toggles the Balloon open/close state and sets activeElement state to -1.
   */


  const toggleBalloon = () => {
    /**
     * Focus the trigger node only when the
     * click happens within the balloon.
     *
     * Focus should not be on the trigger node when
     * click happens else where on the page.
     * Meaning on any interactive element on the page.
     */
    thisRef.current.activeElementFocusable = isActiveElementFocusable();

    if (children) {
      let initialBalloonWidth;
      /**
       * The balloon is closing. If it is closing due to the user having
       * scrolled the document, reset the scroll count and focus the
       * trigger.
       */

      if (open) {
        if (thisRef.current.scrollCount > 0) {
          thisRef.current.scrollCount = 0;
          thisRef.current.triggerNode && thisRef.current.triggerNode.focus();
        }
      }
      /**
       * If the current open state of balloon is false, this means
       * the balloon is about to open. So, doing the necessary calculations
       * for getting the width of the balloon that its content
       * occupy.
       */


      if (!open) {
        var _popupRef$current;

        initialBalloonWidth = selectMenuWidth || triggerNodeWidth;
        thisRef.current.scrollCount = 0;
        setDisplayErrors(false);
        (_popupRef$current = popupRef.current) === null || _popupRef$current === void 0 ? void 0 : _popupRef$current.setTriggerRef(thisRef.current.triggerNode);
      } else if (errors[name] && !displayErrors && selectRef.current.value === "") {
        setDisplayErrors(true);
      } else {
        setDisplayErrors(false);
      }

      setBalloonWidth(initialBalloonWidth);
      setOpen(!open);
      setActiveIndex(-1);
    }
  };

  useOnEscape(toggleBalloon, open);
  /**
   * This method is responsible for updating currentIndex value to focus on typed Item in Select.
   * Updates the searchMatch value to 'true' if it identifies the match.
   *
   * @param {array} items This holds enabled menu items.
   * @param {number} startIndex This refers the menu item start index.
   * @param {number} itemsLength This holds the menu items length.
   */

  const findMatchedIndex = (items, startIndex, itemsLength) => {
    const character = thisRef.current.searchQuery;

    for (let currentIndex = startIndex; currentIndex < itemsLength; currentIndex++) {
      var _items$currentIndex;

      const characterToMatch = (_items$currentIndex = items[currentIndex]) === null || _items$currentIndex === void 0 ? void 0 : _items$currentIndex.substr(0, character.length);

      if ((characterToMatch === null || characterToMatch === void 0 ? void 0 : characterToMatch.toLowerCase()) === character.toLowerCase()) {
        return {
          searchIndex: currentIndex,
          searchMatch: true
        };
      }
    }

    return null;
  };
  /*
   * Focus the option based on the typed characters and nullify the search query string
   *
   */


  const focusOnKeyPress = () => {
    const items = thisRef.current.itemsText;
    const itemsLength = items.length;
    let findIndex;
    const searchIndex = items.findIndex((_item, index) => index === activeIndex);
    findIndex = findMatchedIndex(items, searchIndex + 1, itemsLength);

    if (!findIndex) {
      findIndex = findMatchedIndex(items, 0, searchIndex);
    }

    if (findIndex && findIndex.searchMatch && activeIndex !== findIndex.searchIndex) {
      setActiveIndex(findIndex.searchIndex);
    }

    thisRef.current.searchQuery = "";
  };
  /**
   * Updates native select state.
   */


  const updateNativeSelectState = () => {
    validateField && validateField(name);
  };
  /**
   * Handler to be executed whenever click event is fired on any DOM element.
   */


  const handleClick = e => {
    const {
      target,
      type
    } = e;
    const {
      balloonNode
    } = thisRef.current;
    setElementToFocusOnClose(target);
    /**
     * Hide the menu if the click event was fired on an element
     * outside the menu.
     */

    if (balloonNode && !balloonNode.contains(target)) {
      toggleBalloon();
    } // In order to validate on `document.body` click force the event on <select>.


    if (selectRef.current && target !== selectRef.current && type === "click") {
      updateNativeSelectState();
    }
  };
  /**
   * Handler to be executed whenever scroll event is fired on any DOM element.
   */


  const handleScroll = e => {
    const {
      target
    } = e;
    const {
      balloonNode
    } = thisRef.current;
    /**
     * scrollCount is used to differentiate between a user scrolling and the
     * window being scrolled due to the menu opening.
     */

    thisRef.current.scrollCount += 1;

    if (thisRef.current.scrollCount > 2) {
      /**
       * Hide the menu if the scroll event was fired on an element
       * outside the menu unless screen size is less than 320 (Zoom 400%),
       */
      if (!(window.innerWidth <= ZOOM_400_MAX_WIDTH) && balloonNode && !balloonNode.contains(target)) {
        toggleBalloon();
      }
    }
  };

  const handleWindowResizeDebounced = debounce(toggleBalloon, RESIZE_DEBOUNCE_TIME);
  const handleSearchTextDebounced = debounce(focusOnKeyPress, SEARCH_DEBOUNCE_TIME);
  const handleScrollThrottled = throttle(handleScroll, SCROLL_THROTTLE_TIME);
  /**
   * Get the `aria-label` for the Select button.
   * @param {String} label The label text
   * @returns String
   */

  const getAriaLabel = labelText => {
    let additionalInfo, selectArialLabel;
    /**
     * HTML required and aria-required are not supported on button controls.
     * For this reason, this state information must be provided via the aria-label.
     * Storing this information in `additionalInfo` variable below.
     */

    if (required) {
      additionalInfo = REQUIRED_TEXT;
    }

    if (ariaLabel) {
      return ariaLabel(labelText, selectedOptionLabel, additionalInfo);
    }
    /**
     * If label is present prepend label with selected option text.
     * Else show only the selected option text
     */


    selectArialLabel = labelText ? `${labelText}, ${selectedOptionLabel}` : selectedOptionLabel;

    if (additionalInfo) {
      selectArialLabel = `${selectArialLabel}, ${additionalInfo}`;
    } // make sure we return a string, even if label is a number


    return selectArialLabel.toString();
  };
  /**
   * Sets the aria-label for the trigger with the textContent of the label element
   * if an element has been provided for the label prop.
   */


  const setAriaLabel = () => {
    if (typeof label === "object") {
      const labelText = labelRef.current && labelRef.current.textContent;
      thisRef.current.triggerNode.setAttribute("aria-label", getAriaLabel(labelText));
    }
  };
  /**
   * Adds event listeners.
   */


  const addEventListeners = () => {
    const {
      scrollListenerTimeout
    } = thisRef.current;
    window.addEventListener("resize", handleWindowResizeDebounced);
    /**
     * On opening the select balloon, we need to hide the `FieldError`.
     * But, when the page is having a scrollbar and if it is scrolled to bottom
     * of the page, toggling the `FieldError` component is firing the
     * scroll event in the GC browser only. In order to prevent the
     * firing of scroll event, delaying the binding of scroll handler.
     */

    if (scrollListenerTimeout) {
      clearTimeout(scrollListenerTimeout);
    }

    thisRef.current.scrollListenerTimeout = setTimeout(() => {
      document.addEventListener("scroll", handleScrollThrottled, true);
    }, SCROLL_ATTACH_TIME);
    document.addEventListener("click", handleClick, true);
  };
  /**
   * Removes event listeners attached to the DOM.
   */


  const removeEventListeners = () => {
    window.removeEventListener("resize", handleWindowResizeDebounced);
    document.removeEventListener("scroll", handleScrollThrottled, true);
    document.removeEventListener("click", handleClick, true);
  };
  /**
   * Returns the label for Button element.
   * @param {String} selectedValue
   * @returns {String}
   */


  const getButtonLabel = selectedValue => {
    return itemsMap.current[selectedValue] || disabledItemsMap.current[selectedValue] || placeholder;
  };
  /**
   * Native hidden Select element value has to be updated everytime,
   * whenever selected item is changed either programmatically or manually.
   * Hence no dependencies are added for this effect.
   */


  useEffect(() => {
    setAriaLabel();
    selectRef.current.value = currentSelectedValue;
  });
  useEffect(() => {
    var _popupRef$current2;

    const trigger = thisRef.current.triggerNode;
    /**
     * Storing the `triggerNodeWidth` in state in order to
     * access its value inside the state update `callback`.
     */

    const {
      width: triggerWidth
    } = getElementWidth(trigger);
    setTriggerNodeWidth(triggerWidth);
    (_popupRef$current2 = popupRef.current) === null || _popupRef$current2 === void 0 ? void 0 : _popupRef$current2.setTriggerRef(trigger);
  }, [thisRef.current.triggerNode]);
  useEffect(() => {
    const {
      scrollListenerTimeout
    } = thisRef.current;
    return () => {
      handleWindowResizeDebounced.cancel();
      handleSearchTextDebounced.cancel();
      handleScrollThrottled.cancel();
      removeEventListeners();
      clearTimeout(scrollListenerTimeout);
      document.body.classList.remove(SELECT_BACKGROUND_SCROLL_CLASS);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propDefaultValue, currentSelectedValue, open, children]);
  useEffect(() => {
    // Should execute only on change in open value, not on mount.
    if (open === null) return;
    const {
      activeElementFocusable,
      triggerNode
    } = thisRef.current; // toggle(add/remove) event handlers (change, resize and scroll) based on balloon's visibility

    if (open) {
      /**
       * On mobile breakpoint, when Select List is opened,
       * the background scroll is locked to prevent unintended scrolling of the page,
       * as well as to make it easier to scroll through a long list of items.
       */
      if (window.innerWidth <= MOBILE_BREAKPOINT) {
        document.body.classList.add(SELECT_BACKGROUND_SCROLL_CLASS);
      }

      setActiveIndex(thisRef.current.currentIndex);
      addEventListeners();
    } else {
      document.body.classList.remove(SELECT_BACKGROUND_SCROLL_CLASS);
      removeEventListeners();
      /**
       * When focus is within the options balloon,
       * and the user clicks on an interactive/focusable element
       * outside of it, then focus has to be moved
       * to interactive element rather than `triggerNode`.
       * Below condition will prevent focusing the
       * `triggerNode`.
       */

      if (!activeElementFocusable && !open) {
        triggerNode.focus();
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [open]);
  const {
    buttonCSS,
    buttonErrorCSS,
    buttonWrapperCSS,
    hintCSS,
    instructionalTextCSS,
    menuItemCSS,
    menuItemDisabledCSS,
    menuItemGroupCSS,
    menuItemSelectedCSS,
    menuItemWrapCSS,
    selectFooterCSS,
    selectFooterErrorCSS,
    selectLabelCSS,
    selectOpenedCSS,
    selectLabelInlineCSS,
    selectLabelErrorCSS,
    selectLabelNoEllipsisCSS,
    selectRoot,
    selectMenuCSS,
    selectWrapperStyles,
    subGroupIconCSS
  } = extractTheme({
    theme
  });
  const {
    floatingLabel
  } = selectRoot;

  const selectButtonStyles = _objectSpread(_objectSpread(_objectSpread({}, errors[name] && displayErrors ? safeMerge(buttonCSS, buttonErrorCSS) : buttonCSS), open ? selectOpenedCSS : []), selectTriggerStyles);

  const labelClassNames = clsx(labelClassNameProp, {
    "wf-u-text-truncate": !noEllipsisForPlaceholder && floatingLabel,
    "wf-u-visually-hidden": labelHidden
  });
  const labelLeftRightSpacing = 16;
  const IconWidth = 24;

  const labelStyles = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, errors[name] && displayErrors ? safeMerge(selectLabelCSS, selectLabelErrorCSS) : selectLabelCSS), labelClassNameProp ? selectLabelInlineCSS : []), isDisabled ? selectLabelCSS.disabled : []), noEllipsisForPlaceholder ? selectLabelNoEllipsisCSS : []), {}, {
    width: floatingLabel ? triggerNodeWidth - labelLeftRightSpacing - IconWidth : null
  });
  /**
   * Triggers the onBlur handler passed by the user to the Select Component.
   */


  const handleBlur = e => {
    /**
     * Preventing the execution when the user
     * is clicking the `Select` trigger node to open the balloon.
     *
     * If the balloon is opened and blur is fired,
     * then the execution should not be prevented.
     */
    if (open) {
      return;
    } // Force the `onBlur` event on the actual <select> in order to validate.


    const {
      relatedTarget
    } = e;

    if (e.target !== selectRef.current) {
      updateNativeSelectState();
      relatedTarget && relatedTarget.focus && relatedTarget.focus();
    }

    onBlur && onBlur(e);
    /**
     * On blur, we need to show errors (if there are any).
     */

    !displayErrors && setDisplayErrors(true);
    setIsBlurred(true);
  };

  useEffect(() => {
    if (validateTimestamp) {
      /**
       * A manual validation was triggered at the form level.
       * That means we need to show error on field even if it's not
       * dirty (blurred) yet.
       */
      if (errors[name]) {
        !displayErrors && setDisplayErrors(true);
        setIsBlurred(true);
      }
    }
  }, [displayErrors, errors, name, validateTimestamp]);
  /**
   * Show errors if the Form is being submitted even if the field
   * hasn't been blurred yet.
   */

  useEffect(() => {
    isSubmitting && setDisplayErrors(true);
  }, [isSubmitting]);
  /**
   * On reset, hide errors until a new blur occurs and
   * reset the selected value.
   */

  useEffect(() => {
    if (resetDone) {
      setDisplayErrors(false);
      setSelectedValue("");
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [resetDone]);
  useEffect(() => {
    if (isBlurred) {
      setIsBlurred(false);
    }
  }, [isBlurred]);
  /**
   * React Hook Form will run all validators on blur of an empty select.
   * This isn't desired, so this function creates a new set of custom
   * validators that first check for an empty value, returning valid
   * if this is the case.
   * @param {Array} customValidators Original customValidator Array.
   * @returns {Array} Array of updated validators.
   */

  const getUpdatedValidators = customValidators => {
    const newValidators = [];

    if (customValidators) {
      customValidators.forEach(customValidator => {
        const newValidator = function () {
          if ((arguments.length <= 0 ? undefined : arguments[0]) === "") return true;
          return customValidator(...arguments);
        };

        newValidators.push(newValidator);
      });
    }

    return newValidators;
  };
  /**
   * Registering our field with the "register" method from
   * react-hook-form.
   */


  const {
    inputRef,
    reactHookFormRest
  } = registerField({
    register,
    name,
    required,
    requiredErrorMessage,
    customValidators: getUpdatedValidators(customValidators)
  });
  useEffect(() => {
    return () => {
      /**
       * Component is being unmounted.
       * Un-registering field with the "unregister" method
       * from react-hook-form.
       */
      unregister && unregister(name);
    };
  }, [name, unregister]);

  const setTriggerNode = nativeButton => {
    thisRef.current.triggerNode = nativeButton;
    triggerRef.current = nativeButton;

    if (userRef) {
      userRef.current = nativeButton;
    }
  };

  const setBalloonRef = el => {
    thisRef.current.balloonNode = el && el.contentRef.current;
    popupRef.current = el;
  };
  /**
   * This method is responsible for updating the selected value in the state and for the native-select.
   * As the selection is made in the balloon, we need to set the value for the native select, notify
   * React Hook Form of the change, and trigger a Form validation.
   */


  const handleSelection = (e, menuItemValue) => {
    selectedOptionLabel = getButtonLabel(menuItemValue);
    selectRef.current.value = menuItemValue;
    setValue && setValue(name, menuItemValue);
    trigger && trigger(name);
    setSelectedValue(menuItemValue);
    toggleBalloon();
    e.target = selectRef.current;
    const {
      value,
      id,
      type
    } = e.target;
    onValueChange && onValueChange({
      e,
      value,
      name,
      id,
      type
    });
    onChange && onChange(e);
    thisRef.current.triggerNode.focus();
  };
  /**
   * Prevents the default behavior when the user presses Tab on one of the options.
   */


  const handleOptionKeyDown = e => {
    if (e.key === KEYBOARD_KEYS.TAB) {
      e.preventDefault();
    }
  };
  /**
   * @description Computes the correct height of the menu
   * and updates the state of the balloonMaxHeight
   */


  const setBalloonMaxHeightAndPosition = () => {
    const {
      TOP,
      BOTTOM
    } = BALLOON_POSITION;
    const inputBox = thisRef.current.triggerNode.getBoundingClientRect();
    const distanceFromTop = inputBox.top;
    const distanceFromBottom = window.innerHeight - inputBox.bottom;
    const balloonPosition = distanceFromTop > distanceFromBottom ? TOP : BOTTOM;
    const balloonMaxHeight = Math.ceil((balloonPosition === TOP ? distanceFromTop - TEMPORARY_HEIGHT_BUFFER : distanceFromBottom) * NINETY_PERCENT);
    setBalloonMaxHeight(balloonMaxHeight);
    setBalloonPosition(balloonPosition);
  };
  /**
   * This method is responsible for cloning MenuItem and wraps with KeyboardNavigator.
   * Adds the selected and disabled class.
   * Sets activeElement index for KeyboardNavigator.
   *
   * @param {object} child This holds value, label and disabled property of MenuItem
   * @param {number} focusIndex This is used to set the activeElement index for KeyboardNavigator
   * @param {boolean} nestedItem This will help whether to add the sub account icon for a nested list item
   * @param {number} groupId This id has to be passed to nested MenuItem's aria-describedby
   * @return {React.Element} Returns MenuItem wrapped inside Keyboard Navigator item
   */


  const cloneMenuItem = (child, focusIndex, nestedItem, groupId) => {
    const {
      value: menuItemValue,
      children: menuItemLabel,
      disabled: isMenuItemDisabled,
      wrap: wrapItem,
      className: menuItemClassName
    } = child.props;
    /**
     * Always item wrap prop has to take precedence over parent wrap prop.
     */

    let shouldWrap = null;

    if (wrap || wrapItem) {
      shouldWrap = wrapItem !== false;
    }

    const isItemSelected = (menuItemValue === null || menuItemValue === void 0 ? void 0 : menuItemValue.toString()) === (currentSelectedValue === null || currentSelectedValue === void 0 ? void 0 : currentSelectedValue.toString());

    const menuItemWrapperStyles = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, menuItemCSS), shouldWrap ? menuItemWrapCSS : []), isItemSelected && !isMenuItemDisabled ? menuItemSelectedCSS : []), isMenuItemDisabled ? menuItemDisabledCSS : []), floatingLabel && focusIndex === 0 && instructionalText ? instructionalTextCSS : []);

    const subAccountIconSpan = isItemSelected && !floatingLabel ? _jsx("span", {
      css: subGroupIconCSS,
      children: _jsx(IconSubAccount, {
        size: "xsmall",
        theme: "inverse"
      })
    }, menuItemValue) : _jsx("span", {
      css: subGroupIconCSS,
      children: _jsx(IconSubAccount, {
        size: "xsmall"
      })
    }, menuItemValue);
    isItemSelected && (thisRef.current.currentIndex = isMenuItemDisabled ? 0 : focusIndex);
    const extensions = {
      key: menuItemValue,
      value: menuItemValue,
      onSelect: e => {
        if (child.props.onSelect) {
          child.props.onSelect(e, menuItemValue);
        }

        handleSelection(e, menuItemValue);
      },
      onKeyDown: handleOptionKeyDown,
      role: "option",
      "aria-checked": isItemSelected ? true : null,
      className: `${menuItemClassName}`,
      keyboardNavigateListItem: true,
      wrap: shouldWrap
    };
    extensions.menuItemStyles = {
      menuItemWrapperCSS: menuItemWrapperStyles
    };

    if (nestedItem) {
      extensions.children = [subAccountIconSpan, menuItemLabel];
      extensions["aria-describedby"] = groupId;
    }

    const clonedElement = /*#__PURE__*/React.cloneElement(child, extensions);

    if (isMenuItemDisabled) {
      /**
       * As KBN is used in controlled and it is maintaining the disabled items in focusableElements array.
       * In order to get the right element position in findIndexMatch method,
       * we need to add null at disabled item index.
       */
      thisRef.current.itemsText.push(null);
      disabledItemsMap.current[menuItemValue] = menuItemLabel;
    } else {
      thisRef.current.itemsText.push(menuItemLabel);
      itemsMap.current[menuItemValue] = menuItemLabel;
    }

    return clonedElement;
  };
  /**
   * This method is responsible for looping around children and clone MenuItem and MenuItemGroup list
   * Also, generates option and optgroup list for native select.
   *
   * @return {React.Element} Returns MenuItemGroup and MenuItem wrapped inside Keyboard Navigator item
   */


  const getListFromChildren = () => {
    const tempListItems = {
      liList: [],
      optionsList: []
    };
    let focusIndex = -1;
    thisRef.current.itemsText = [];
    itemsMap.current = {};

    if (floatingLabel && instructionalText) {
      const instructionalTextLabel = `${instructionalText}${!required ? " (optional)" : ""}`;

      const instructionalTextOption = _jsx(MenuItem, {
        value: "",
        children: instructionalTextLabel
      }, "");

      focusIndex++;
      tempListItems.liList.push(cloneMenuItem(instructionalTextOption, focusIndex, false, ""));
      tempListItems.optionsList.push(_jsx("option", {
        value: "",
        children: instructionalTextLabel
      }, ""));
    }

    React.Children.map(children, child => {
      const childType = child.type;

      if (childType.displayName === MENUITEM) {
        const {
          value: childValue,
          children: grandChildren
        } = child.props;
        focusIndex++;
        const clonedMenuItem = cloneMenuItem(child, focusIndex, false, "");
        tempListItems.liList.push(clonedMenuItem); // create options list for native-select

        tempListItems.optionsList.push(_jsx("option", {
          value: childValue,
          children: grandChildren
        }, childValue));
      } else if (childType.displayName === MENUITEMGROUP) {
        const {
          label: groupLabel,
          children: groupChildren,
          id: userPassedGroupId,
          wrap: wrapGroup
        } = child.props;

        const groupId = userPassedGroupId || __uniqueID("pnr-select__group-");

        const nestedItems = React.Children.map(groupChildren, childItem => {
          focusIndex++;
          return cloneMenuItem(childItem, focusIndex, true, groupId);
        });
        /**
         * Always item group wrap prop has to take precedence over parent wrap prop.
         */

        let shouldWrap = null;

        if (wrap || wrapGroup) {
          shouldWrap = wrapGroup !== false;
        }

        const groupExtensions = {
          key: groupLabel,
          role: "presentation",
          "aria-hidden": true,
          id: groupId,
          expanded: true,
          expandable: false,
          children: nestedItems,
          wrap: shouldWrap
        };
        groupExtensions.menuItemGroupStyles = menuItemGroupCSS;
        tempListItems.liList.push( /*#__PURE__*/React.cloneElement(child, groupExtensions)); // create options list for native-select

        tempListItems.optionsList.push(_jsx(OptGroup, {
          label: groupLabel,
          children: groupChildren
        }, groupLabel));
      }

      return null;
    });
    return tempListItems;
  };

  const listItems = children && getListFromChildren();
  selectedOptionLabel = getButtonLabel(currentSelectedValue);
  /**
   * Add the typed characters to the search query.
   *
   * @param e {EventFacade} The event object
   */

  const handleKeyPress = e => {
    const key = e.key;
    thisRef.current.searchQuery += key;
    handleSearchTextDebounced();
  };
  /**
   * To update the active item's index based on user interaction.
   * This method will be triggered by the KeyboardNavigator
   */


  const handleActiveElementIndexChange = index => {
    thisRef.current.currentIndex = index;
    setActiveIndex(index);
  };

  const handleKeydown = e => {
    switch (e.key) {
      case KEYBOARD_KEYS.LEFT:
      case KEYBOARD_KEYS.RIGHT:
        // Prevent opening/selecting Native Select Options
        e.preventDefault();
        break;

      case KEYBOARD_KEYS.UP:
      case KEYBOARD_KEYS.DOWN:
      case KEYBOARD_KEYS.SPACE:
      case KEYBOARD_KEYS.ENTER:
        // Prevent opening Native Select Options
        e.preventDefault();
        toggleBalloon();
        break;

      default: // DO NOTHING

    }
  };
  /*
   * Prevents the click event from firing
   * `onKeyUp` event in order to prevent select menu
   * from opening again after selection via keyboard
   * in Firefox.
   *
   * @param e {EventFacade} The event object
   */


  const handleKeyUp = e => {
    /**
     * Every browser fires a click event on key down of space
     * on a button. This can be avoided by preventing the default
     * action on `onKeyDown` event handler.
     * But, Firefox does not respect `e.preventDefault()` on the `onKeyDown`
     * event handler (still propagates to a click event).
     * so we need to prevent the default action on `onKeyUp`
     * event handler in order to not propagate a click event.
     * This prevents the select menu from opening again after a selection
     * in Firefox.
     * https://github.com/downshift-js/downshift/issues/471
     */
    e.preventDefault();
  };

  const id = userPassedId || `pnr-select-${uniqueId}`;
  const labelId = `pnr-select__label-${uniqueId}`;

  const getLabel = _jsxs("label", {
    id: labelId,
    "aria-hidden": "true",
    htmlFor: triggerId,
    className: labelClassNames,
    css: labelStyles,
    ref: labelRef,
    children: [required && !labelHidden && !noAsterisk && !floatingLabel ? _jsx(IconRequired, {
      display: "inline-block",
      size: "xsmall",
      spacing: {
        r: 0.5
      }
    }) : null, label]
  });

  const getSelectNode = () => {
    const extraParams = {};

    if (errors[name]) {
      extraParams["aria-describedby"] = errorId;
    } else if (hint) {
      extraParams["aria-describedby"] = hintId;
    }

    return _jsxs(React.Fragment, {
      children: [_jsxs("select", _objectSpread(_objectSpread(_objectSpread({
        className: "wf-u-visually-hidden",
        hidden: true,
        id: id,
        "aria-hidden": "true",
        ref: e => {
          /**
           * Normally, react-hook-form does that automatically.
           * In this case, we want to share the ref with the user, so
           * we need to run this ourselves.
           *
           * See "How to share ref usage?" in the FAQs.
           * https://react-hook-form.com/faqs
           */
          inputRef && inputRef(e);
          selectRef.current = e;

          if (userRef) {
            userRef.current = e;
          }
        },
        required: required ? required : null,
        name: name
      }, elementProps), reactHookFormRest), {}, {
        children: [!propDefaultValue && _jsx("option", {
          value: ""
        }), listItems && listItems.optionsList]
      })), floatingLabel && getLabel, _jsx(Button, _objectSpread(_objectSpread(_objectSpread({
        id: triggerId,
        ref: setTriggerNode,
        kind: "standard",
        onKeyDown: handleKeydown,
        onKeyUp: handleKeyUp,
        onClick: toggleBalloon,
        onBlur: handleBlur,
        disabled: isDisabled,
        "aria-haspopup": "listbox",
        "aria-expanded": Boolean(open),
        "aria-label": getAriaLabel(label),
        rightIcon: floatingLabel ? _jsx(IconExpand, {
          size: expandIconSize
        }) : _jsx(IconArrowClose, {
          size: "xsmall"
        }),
        css: buttonWrapperCSS,
        buttonStyles: selectButtonStyles,
        fullWidth: true
      }, extraParams), elementProps), {}, {
        children: selectedOptionLabel
      }))]
    });
  };

  const keyboardNavigatorRef = useRef();

  const balloon = children && _jsx(Popup, _objectSpread(_objectSpread({
    on: ["click"],
    ref: setBalloonRef,
    balloonContentRef: keyboardNavigatorRef,
    onOpen: setBalloonMaxHeightAndPosition,
    open: open,
    id: popupId,
    position: balloonPosition,
    zIndex: menuzIndex,
    noArrow: true,
    onKeyPress: handleKeyPress,
    type: "balloon",
    noRole: true,
    elementToFocusOnClose: () => elementToFocusOnClose,
    repositionOnResize: true,
    rootNode: wrapperNodeRef.current,
    setWidthToTrigger: !autoMenuWidth,
    contentStyle: {
      width: autoMenuWidth ? "auto" : `${balloonWidth}px`,
      maxHeight: balloonMaxHeight
    }
  }, elementProps), {}, {
    children: _jsx(KeyboardNavigator, {
      role: "listbox",
      "aria-labelledby": labelId,
      component: "ul",
      css: selectMenuCSS,
      tabIndex: "-1",
      mode: KEYBOARD_NAVIGATOR_MODE.VERTICAL,
      activeElement: activeIndex,
      onActiveElementChange: handleActiveElementIndexChange,
      ref: keyboardNavigatorRef,
      disableCyclingFocus: true,
      className: OVERRIDE,
      children: listItems && listItems.liList
    })
  }));

  const Field = _jsxs("div", {
    css: selectWrapperStyles,
    ref: wrapperNodeRef,
    children: [balloon, !floatingLabel && getLabel, getSelectNode(), _jsx(SelectFooter, {
      displayErrors: displayErrors,
      errors: errors,
      errorId: errorId,
      hint: hint,
      hintId: hintId,
      hintCSS: hintCSS,
      label: label,
      name: name,
      footerErrorStyle: selectFooterErrorCSS,
      footerStyle: selectFooterCSS
    })]
  });
  /**
   * Keep disabled state current with disabled prop changes.
   */


  useEffect(() => {
    setIsDisabled(disabled);
  }, [disabled]);
  /**
   * This useEffect updates the React Hook Form's value of this
   * Select. It is updated when the Select is used in a controlled way
   * where the value prop is updated.
   */

  useEffect(() => {
    if (setValue) {
      setValue(name, currentSelectedValue);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [currentSelectedValue]);
  /**
   * Listen for changes to enclosing fieldset disabled state.
   */

  useEffect(() => {
    const enclosingFieldset = selectRef.current.closest("fieldset");

    if (enclosingFieldset) {
      if (enclosingFieldset.disabled) {
        setIsDisabled(true);
      }

      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.attributeName === "disabled") {
            setIsDisabled(enclosingFieldset.disabled);
          }
        });
      });
      observer.observe(enclosingFieldset, {
        attributes: true
      });
      return () => observer.disconnect();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);
  return _jsx("div", {
    className: containerClassNames,
    css: selectRoot,
    children: _jsx(React.Fragment, {
      children: _jsx("div", {
        className: fieldClassName,
        children: Field
      })
    })
  });
});
Select.propTypes = process.env.NODE_ENV !== "production" ? {
  /** The name of the select field. */
  name: PropTypes.string.isRequired,

  /**
   * A function can be passed to this prop and the return value of this function
   * will be set as the `aria-label` of the Select Button.
   * The function passed to this prop will receive the following parameters:
   *
   * <ul>
   *  <li>
   *    `selectFieldLabel`: The label of the Select field.
   *  </li>
   *  <li>
   *    `selectedOptionLabel`: The currently selected option label.
   *    If no option is selected, then this is equal to default placeholder text "Select".
   *  </li>
   *  <li>
   *    `additionalInfo`: If the Select field is required, then this is equal to "required",
   *     or, if the selection is invalid, then this is equal to "invalid selection".
   *  </li>
   * </ul>
   */
  ariaLabel: PropTypes.func,

  /**
   * Determines whether to apply `width: auto` to balloon container or not.
   * If applied, the width of the balloon will be determined by the
   * contents of balloon, rather than width of the trigger.
   * <br/>
   * If `autoMenuWidth` is applied, the `wrap` prop will have no effect.
   * <br/>
   * `autoMenuWidth` cannot be used individually at the [MenuItem](#/Component%20helpers/MenuItem) level.
   */
  autoMenuWidth: PropTypes.bool,

  /**
   * Select accepts only [MenuItem](#/Component%20helpers/MenuItem) and
   * [MenuItemGroup](#/Component%20helpers/MenuItemGroup) components as children.
   */
  children: invalidIf(PropTypes.node, (props, propName) => {
    let error = false;
    const prop = props[propName];
    React.Children.map(prop, child => {
      const childrenType = child.type;

      if (childrenType.displayName !== MENUITEM && childrenType.displayName !== MENUITEMGROUP) {
        error = true;
      }
    });
    return error;
  }, `'Select' children should be of type 'MenuItem' or 'MenuItemGroup'.`),

  /** The CSS class name of the container that wraps both label and select field. */
  className: PropTypes.string,

  /**
   * Override for strings used within the component.
   */
  customStrings: PropTypes.shape({
    /**
     * Instructional text that will be shown as an option.
     */
    instructionalText: PropTypes.string,

    /**
     * Default select field text.
     */
    placeholder: PropTypes.string,

    /**
     * Error message displayed when a required field is invalid.
     */
    requiredErrorMessage: PropTypes.string
  }),

  /**
   * Adds custom validator(s) to the Select and then calculates the validity
   * of the Select on value change.
   *
   * An array of functions that accept the value of a Select and returns an error
   * string if the Select becomes invalid.
   *
   * This prop cannot be used to check if a Select field is empty. Please use the prop `required` instead.
   */
  customValidators: PropTypes.arrayOf(PropTypes.func),

  /** The default value for the select field. */
  defaultValue: PropTypes.string,

  /** Whether the select field is disabled or not. */
  disabled: PropTypes.bool,

  /**
   * A callback to achieve update of errors to Form.
   */
  errorMessageCallback: PropTypes.func,

  /**
   * To amend the size of the icon when `Select` component
   * is used internally by other components like `Calendar`.
   */
  expandIconSize: PropTypes.string,

  /** The CSS class name of the container that wraps select field. */
  fieldClassName: PropTypes.string,

  /** The example/hint text for select field. */
  hint: PropTypes.node,

  /** The select id value. While optional, if an id is not added, one will be generated. */
  id: PropTypes.string,

  /** This is the label associated with the select field. */
  label: PropTypes.node,

  /** The CSS class name of the container that wraps the label. */
  labelClassName: PropTypes.string,

  /** This prop will hide the label visually but retain accessible relationship with the select field. */
  labelHidden: PropTypes.bool,

  /** The zIndex of the balloon in relation to other elements on the page. */
  menuzIndex: PropTypes.number,

  /**
   * The following keys are mirror properties of the `ValidityState` interface.
   * This facilitates to customize validation messages where native html validation is used.
   *
   * https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
   */
  nativeValidationMessages: PropTypes.shape({
    badInput: PropTypes.string,
    patternMismatch: PropTypes.string,
    rangeOverflow: PropTypes.string,
    rangeUnderflow: PropTypes.string,
    stepMismatch: PropTypes.string,
    tooLong: PropTypes.string,
    tooShort: PropTypes.string,
    typeMismatch: PropTypes.string,
    valueMissing: PropTypes.string
  }),

  /**
   * Determines whether to add an asterisk to the left of the label of a required Select field.
   */
  noAsterisk: PropTypes.bool,

  /**
   * Determines whether the ellipsis should be shown for Placeholder or not.
   */
  noEllipsisForPlaceholder: PropTypes.bool,

  /**
   * Callback to be executed on blur of the Select.
   * @param {EventFacade} e Receives the blur event facade as a parameter.
   * @param {Function} setErrorMessage Receives the function which when called
   * with the error message passed as a string, displays the error message
   * for the Select field.
   */
  onBlur: PropTypes.func,

  /**
   * Callback to be executed on change of the selection from the select element.
   * @deprecated As of release 3.6.0, use `onValueChange` instead.
   */
  onChange: PropTypes.func,

  /**
   * Callback to be executed on value change of the select field.
   * It should be exposed to support controlled way.
   * The onValueChange function differs from onChange in that it receives an object
   * with the following signature as a parameter. {e, value, name, id}
   * @param {Object} object signature {e, value, name, id}
   */
  onValueChange: PropTypes.func,

  /** The prop will determine if select field is required. */
  required: PropTypes.bool,

  /**
   * The width of the Select Menu Balloon in pixels.
   */
  selectMenuWidth: PropTypes.number,

  /**
   * To override the styles of the `Select` trigger node
   * that is `BaseButton`. It is used by internal components
   * only like `Calendar`.
   */
  selectTriggerStyles: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /** The value of the selected option. It should be used for controlled component. */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * Wrap the content of each option if the content does not fit the width of the container.
   * If not set, the content will be truncated with an ellipsis.
   * <br/>
   * `wrap` can also be used individually at the [MenuItem](#/Component%20helpers/MenuItem) level.
   */
  wrap: invalidIf(PropTypes.bool, (props, propName) => props[propName] === true && props.autoMenuWidth === true, `when you supply "props.autoMenuWidth".`)
} : {};
Select.defaultProps = {
  children: null,
  className: "",
  customStrings: null,
  customValidators: null,
  defaultValue: null,
  disabled: false,
  expandIconSize: "small",
  fieldClassName: "",
  id: null,
  label: null,
  labelClassName: "",
  labelHidden: false,
  hint: null,
  nativeValidationMessages: STRINGS.nativeValidationMessages,
  onChange: null,
  onValueChange: null,
  onBlur: null,
  required: false,
  value: null,
  noAsterisk: false,
  ariaLabel: null,
  selectMenuWidth: null,
  selectTriggerStyles: {},
  noEllipsisForPlaceholder: false,
  wrap: false,
  autoMenuWidth: false,
  menuzIndex: ZINDEX.BASE_BALLOON,
  errorMessageCallback: null
};
Select.displayName = "Select";
export default Select;